'use client';

import { type SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { isProductionEnvironment } from '../utils/is-production-environment';
import { useCallbackRef } from './use-callback-ref';

type ChangeHandlerType<TValue, TChangeArgs extends ReadonlyArray<unknown>> = (
  value: TValue,
  ...changeArgs: TChangeArgs
) => void;

export type SetControllableValueType<TValue, TChangeArgs extends ReadonlyArray<unknown> = []> = (
  nextValue: SetStateAction<TValue>,
  ...changeArgs: TChangeArgs
) => void;

export interface UseControllableValueOptionsInterface<
  TValue,
  TChangeArgs extends ReadonlyArray<unknown> = [],
> {
  /**
   * The value used while the component is controlled.
   */
  controlledValue: TValue;

  /**
   * The initial value used while the component is uncontrolled.
   */
  uncontrolledValue: TValue | (() => TValue);

  /**
   * Whether the consumer opted into controlled ownership.
   */
  isControlled: boolean;

  /**
   * Whether the consumer opted into uncontrolled ownership.
   */
  isUncontrolled: boolean;

  /**
   * Callback called when a setter requests a value change.
   */
  onChange?: ChangeHandlerType<TValue, TChangeArgs>;

  /**
   * Component name used in development warnings.
   */
  componentName?: string;

  /**
   * Controlled prop name used in development warnings.
   */
  controlledPropName?: string;

  /**
   * Uncontrolled prop name used in development warnings.
   */
  uncontrolledPropName?: string;
}

/**
 * Hook for components that can be either controlled by props or uncontrolled with local state.
 * Controlled ownership is explicit and does not depend on whether the current value is
 * `undefined`. Components may still normalize to concrete empty values when their own runtime
 * behavior requires it.
 * When `isControlled` is `false`, the returned value comes from internal state initialized by
 * `uncontrolledValue`; `controlledValue` does not drive updates across renders. If both
 * `isControlled` and `isUncontrolled` are `false`, the hook is implicitly uncontrolled. Derive
 * ownership flags from the component's original props object before destructuring can remove keys.
 *
 * @template TValue - The state value type.
 * @template TChangeArgs - Extra arguments forwarded to the change handler.
 *
 * @example
 * const [open, setOpen] = useControllableValue<boolean, [event?: Event]>({
 *   controlledValue: props.open ?? false,
 *   uncontrolledValue: props.defaultOpen ?? false,
 *   isControlled: 'open' in props,
 *   isUncontrolled: 'defaultOpen' in props,
 *   onChange: props.onOpenChange,
 * });
 */
export function useControllableValue<TValue, TChangeArgs extends ReadonlyArray<unknown> = []>({
  controlledValue,
  uncontrolledValue,
  isControlled,
  isUncontrolled,
  onChange,
  componentName,
  controlledPropName,
  uncontrolledPropName,
}: UseControllableValueOptionsInterface<TValue, TChangeArgs>) {
  const [uncontrolledState, setUncontrolledState] = useState<TValue>(uncontrolledValue);
  const initialIsControlledRef = useRef(isControlled);
  const hasWarnedModeSwitchRef = useRef(false);
  const hasWarnedMixedPropsRef = useRef(false);
  const handleChange = useCallbackRef<ChangeHandlerType<TValue, TChangeArgs>>(onChange);
  const value = isControlled ? controlledValue : uncontrolledState;
  const valueRef = useRef(value);

  useEffect(() => {
    if (isProductionEnvironment() || hasWarnedModeSwitchRef.current) return;

    if (initialIsControlledRef.current !== isControlled) {
      const from = initialIsControlledRef.current ? 'controlled' : 'uncontrolled';
      const to = isControlled ? 'controlled' : 'uncontrolled';

      // eslint-disable-next-line no-console -- Development-only ownership warning.
      console.warn(
        `${getComponentLabel(componentName)} changed from ${from} to ${to}. Components should not switch between controlled and uncontrolled ownership during their lifetime.`,
      );

      hasWarnedModeSwitchRef.current = true;
    }
  }, [componentName, isControlled]);

  useEffect(() => {
    if (isProductionEnvironment() || hasWarnedMixedPropsRef.current) return;
    if (!initialIsControlledRef.current || !isUncontrolled) return;

    // eslint-disable-next-line no-console -- Development-only ownership warning.
    console.warn(
      `${getComponentLabel(componentName)} received both ${getPropLabel(
        controlledPropName,
        'a controlled value prop',
      )} and ${getPropLabel(
        uncontrolledPropName,
        'an uncontrolled value prop',
      )}. Choose either controlled or uncontrolled ownership for the component lifetime.`,
    );

    hasWarnedMixedPropsRef.current = true;
  }, [componentName, controlledPropName, isUncontrolled, uncontrolledPropName]);

  const setValue = useCallback<SetControllableValueType<TValue, TChangeArgs>>(
    (nextValue, ...changeArgs) => {
      const previousValue = isControlled ? controlledValue : valueRef.current;
      const resolvedValue = isSetStateFunction(nextValue) ? nextValue(previousValue) : nextValue;

      if (Object.is(previousValue, resolvedValue)) return;

      if (!isControlled) {
        valueRef.current = resolvedValue;
        setUncontrolledState(resolvedValue);
      }

      handleChange(resolvedValue, ...changeArgs);
    },
    [controlledValue, handleChange, isControlled],
  );

  return useMemo(() => [value, setValue] as const, [value, setValue]);
}

function isSetStateFunction<TValue>(
  value: SetStateAction<TValue>,
): value is (prevState: TValue) => TValue {
  return typeof value === 'function';
}

function getComponentLabel(componentName: string | undefined) {
  return componentName ? `\`${componentName}\`` : 'A component';
}

function getPropLabel(propName: string | undefined, fallback: string) {
  return propName ? `\`${propName}\`` : fallback;
}
